import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchSeoData } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import SeoSettingsDialog from "@/pages/client-tabs/SeoSettingsDialog";
import {
  ResponsiveContainer, LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";
import {
  Search, Target, Sparkles, ListChecks, ArrowUpRight, ArrowDownRight, Minus, Flag, Globe2, Layers, Calendar, CheckCircle2, Circle, Loader2, AlertCircle, Bot, Settings, Award, MoveRight, Camera, FileWarning, LinkIcon,
} from "lucide-react";
import OtrosSection from "@/pages/client-tabs/OtrosSection";
import KeywordRankingsCard from "@/pages/client-tabs/KeywordRankingsCard";
import GscTopQueriesCard from "@/pages/client-tabs/GscTopQueriesCard";

const COUNTRY_FLAGS = { AR: "🇦🇷", MX: "🇲🇽", ES: "🇪🇸", CL: "🇨🇱", CO: "🇨🇴", US: "🇺🇸", BR: "🇧🇷", PE: "🇵🇪", UY: "🇺🇾" };
// Para For Me se ocultan por ahora: Evolución orgánica, Visibilidad IA y Backlog técnico.
const FORME_CLIENT_ID = "46fffad1-0e87-42bc-8bdc-7132b8ca3502";
const TOOL_TONE = {
  ChatGPT: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", dot: "bg-emerald-500" },
  Perplexity: { bg: "bg-sky-50", border: "border-sky-200", text: "text-sky-700", dot: "bg-sky-500" },
  "Google AIO": { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", dot: "bg-amber-500" },
};

export default function SeoGeoTab({ clientId }) {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ["seo-data", clientId],
    queryFn: () => fetchSeoData(clientId),
  });

  if (isLoading || !data) {
    return (
      <div className="space-y-3" data-testid="seo-loading">
        <div className="h-10 rounded-md skeleton" />
        <div className="h-72 rounded-md skeleton" />
      </div>
    );
  }

  const hasData = data.compromiso || data.initial_snapshot;
  if (!hasData) {
    return (
      <>
        <div data-testid="seo-geo-tab">
          <Tabs defaultValue="compromiso" className="w-full">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
              <TabsList className="h-auto p-1 bg-slate-100 border border-slate-200 rounded-md inline-flex flex-wrap gap-1">
                <TabsTrigger value="compromiso" data-testid="seo-tab-compromiso" className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-600 px-3 py-1.5 rounded-sm text-xs font-medium gap-1.5">
                  <Flag className="h-3.5 w-3.5" /> Compromiso SEO · B2B
                </TabsTrigger>
                <TabsTrigger value="otros" data-testid="seo-tab-otros" className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-600 px-3 py-1.5 rounded-sm text-xs font-medium gap-1.5">
                  <Layers className="h-3.5 w-3.5" /> Otros
                </TabsTrigger>
              </TabsList>
              <Button variant="outline" size="sm" onClick={() => setSettingsOpen(true)} data-testid="seo-config-btn">
                <Settings className="h-3.5 w-3.5 mr-1.5" /> Configurar
              </Button>
            </div>
            <TabsContent value="compromiso" className="mt-0">
              <div className="bg-white border border-dashed border-slate-300 rounded-md p-12 text-center" data-testid="seo-empty">
                <Search className="h-9 w-9 text-slate-400 mx-auto mb-3" />
                <h3 className="font-display text-base font-semibold text-slate-900">Aún no configurado</h3>
                <p className="text-sm text-slate-500 mt-1.5 max-w-md mx-auto">
                  Configurá el compromiso SEO para arrancar el monitoreo de este cliente.
                </p>
                <Button onClick={() => setSettingsOpen(true)} className="mt-4 bg-slate-900 hover:bg-slate-800" data-testid="seo-config-empty-btn">
                  <Settings className="h-4 w-4 mr-1.5" /> Configurar compromiso
                </Button>
                <p className="text-[11px] text-slate-400 mt-3">
                  Mientras tanto, podés ver YouTube en la pestaña <span className="font-semibold">Otros</span>.
                </p>
              </div>
            </TabsContent>
            <TabsContent value="otros" className="mt-0">
              <OtrosSection clientId={clientId} />
            </TabsContent>
          </Tabs>
        </div>
        <SeoSettingsDialog clientId={clientId} seoData={data} open={settingsOpen} onOpenChange={setSettingsOpen} />
      </>
    );
  }

  return (
    <div data-testid="seo-geo-tab">
      <Tabs defaultValue="compromiso" className="w-full">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <TabsList className="h-auto p-1 bg-slate-100 border border-slate-200 rounded-md inline-flex flex-wrap gap-1">
            <TabsTrigger value="compromiso" data-testid="seo-tab-compromiso" className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-600 px-3 py-1.5 rounded-sm text-xs font-medium gap-1.5">
              <Flag className="h-3.5 w-3.5" /> Compromiso SEO · B2B
            </TabsTrigger>
            <TabsTrigger value="day0" data-testid="seo-tab-day0" className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-600 px-3 py-1.5 rounded-sm text-xs font-medium gap-1.5">
              <Camera className="h-3.5 w-3.5" /> Snap de día cero
            </TabsTrigger>
            {clientId !== FORME_CLIENT_ID && (
              <TabsTrigger value="evolution" data-testid="seo-tab-evolution" className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-600 px-3 py-1.5 rounded-sm text-xs font-medium gap-1.5">
                <Target className="h-3.5 w-3.5" /> Evolución orgánica
              </TabsTrigger>
            )}
            {clientId !== FORME_CLIENT_ID && (
              <TabsTrigger value="ai" data-testid="seo-tab-ai" className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-600 px-3 py-1.5 rounded-sm text-xs font-medium gap-1.5">
                <Sparkles className="h-3.5 w-3.5" /> Visibilidad IA
              </TabsTrigger>
            )}
            {clientId !== FORME_CLIENT_ID && (
              <TabsTrigger value="backlog" data-testid="seo-tab-backlog" className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-600 px-3 py-1.5 rounded-sm text-xs font-medium gap-1.5">
                <ListChecks className="h-3.5 w-3.5" /> Backlog técnico
              </TabsTrigger>
            )}
            <TabsTrigger value="otros" data-testid="seo-tab-otros" className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-600 px-3 py-1.5 rounded-sm text-xs font-medium gap-1.5">
              <Layers className="h-3.5 w-3.5" /> Otros
            </TabsTrigger>
          </TabsList>
          <Button variant="outline" size="sm" onClick={() => setSettingsOpen(true)} data-testid="seo-config-btn">
            <Settings className="h-3.5 w-3.5 mr-1.5" /> Configurar
          </Button>
        </div>

        <TabsContent value="compromiso" className="mt-0">
          <CompromisoSection data={data} />
        </TabsContent>
        <TabsContent value="day0" className="mt-0">
          <Day0Section data={data} />
        </TabsContent>
        {clientId !== FORME_CLIENT_ID && (
          <TabsContent value="evolution" className="mt-0">
            <EvolutionSection clientId={clientId} data={data.evolution} initial={data.initial_snapshot} compromiso={data.compromiso} />
          </TabsContent>
        )}
        {clientId !== FORME_CLIENT_ID && (
          <TabsContent value="ai" className="mt-0">
            <AiVisibilitySection rows={data.ai_visibility || []} />
          </TabsContent>
        )}
        {clientId !== FORME_CLIENT_ID && (
          <TabsContent value="backlog" className="mt-0">
            <BacklogSection tasks={data.backlog || []} />
          </TabsContent>
        )}
        <TabsContent value="otros" className="mt-0">
          <OtrosSection clientId={clientId} />
        </TabsContent>
      </Tabs>

      <SeoSettingsDialog clientId={clientId} seoData={data} open={settingsOpen} onOpenChange={setSettingsOpen} />
    </div>
  );
}

/* ============== Compromiso + Estado inicial ============== */
function CompromisoSection({ data }) {
  const c = data.compromiso || {};
  const s = data.initial_snapshot || {};
  const dl = c.deliverables || {};
  const months = c.duration_months || 6;

  const deliverableCards = [
    dl.technical_audits?.target > 0 && {
      key: "audits",
      label: dl.technical_audits.label || "Auditorías técnicas y fixes completos",
      value: `${dl.technical_audits.target}`,
      suffix: "auditorías",
      tone: "indigo",
    },
    (dl.organic_traffic_growth?.min_pct > 0 || dl.organic_traffic_growth?.max_pct > 0) && {
      key: "traffic",
      label: dl.organic_traffic_growth.label || "Crecimiento de tráfico orgánico",
      value: `${dl.organic_traffic_growth.min_pct}–${dl.organic_traffic_growth.max_pct}`,
      suffix: "%",
      tone: "sky",
    },
    dl.new_keywords_page1?.target > 0 && {
      key: "kw",
      label: dl.new_keywords_page1.label || "Nuevas keywords en página 1",
      value: `${dl.new_keywords_page1.target}`,
      suffix: "keywords",
      tone: "emerald",
    },
    (dl.ai_overview_topics?.min > 0 || dl.ai_overview_topics?.max > 0) && {
      key: "aio",
      label: dl.ai_overview_topics.label || "Aparición en AI Overview",
      value: `${dl.ai_overview_topics.min}–${dl.ai_overview_topics.max}`,
      suffix: "temas",
      tone: "violet",
    },
    (dl.quality_backlinks?.min > 0 || dl.quality_backlinks?.max > 0) && {
      key: "bl",
      label: dl.quality_backlinks.label || "Backlinks de calidad",
      value: `${dl.quality_backlinks.min}–${dl.quality_backlinks.max}`,
      suffix: "backlinks",
      tone: "amber",
    },
    (dl.conversion_rate_improvement?.min_pct > 0 || dl.conversion_rate_improvement?.max_pct > 0) && {
      key: "conv",
      label: dl.conversion_rate_improvement.label || "Mejora de tasa de conversión",
      value: `+${dl.conversion_rate_improvement.min_pct}–${dl.conversion_rate_improvement.max_pct}`,
      suffix: "%",
      tone: "rose",
    },
    dl.portals_opened?.min > 0 && {
      key: "portals",
      label: dl.portals_opened.label || "Revisión y apertura de portales",
      value: `≥${dl.portals_opened.min}`,
      suffix: "portales",
      tone: "slate",
    },
    dl.blog_migration?.enabled && {
      key: "blog_mig",
      label: dl.blog_migration.label || "Migración del blog a dominio principal",
      value: "✓",
      suffix: "comprometido",
      tone: "indigo",
      isBool: true,
    },
  ].filter(Boolean);

  return (
    <div className="space-y-6" data-testid="compromiso-section">
      {/* Compromisos a N meses — banner destacado arriba */}
      {deliverableCards.length > 0 && (
        <section className="bg-gradient-to-br from-indigo-50 to-white border border-indigo-200 rounded-md p-5" data-testid="deliverables-section">
          <div className="flex items-start gap-2 mb-4 flex-wrap">
            <Award className="h-4 w-4 text-indigo-600 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h3 className="font-display text-sm font-semibold text-slate-900">Compromisos a {months} meses</h3>
              <p className="text-[11px] text-slate-500 mt-0.5">Lo que prometimos al cliente para el período de monitoreo</p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2.5">
            {deliverableCards.map(({ key, ...rest }) => (
              <DeliverableTile key={key} {...rest} />
            ))}
          </div>
        </section>
      )}

      <div className="grid grid-cols-1 gap-5">
        {/* Lo que prometimos */}
        <section className="bg-white border border-slate-200 rounded-md p-5">
          <div className="flex items-start gap-2 mb-4 flex-wrap">
            <Flag className="h-4 w-4 text-indigo-600 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h3 className="font-display text-sm font-semibold text-slate-900">Lo que prometimos</h3>
              <p className="text-[11px] text-slate-500 mt-0.5">Configuración inicial del monitoreo SEO/GEO/AEO</p>
            </div>
            {c.monitoring_started_at && (
              <span className="text-[10px] font-mono text-slate-500 bg-slate-50 border border-slate-200 px-2 py-0.5 rounded-sm flex items-center gap-1">
                <Calendar className="h-3 w-3" /> Inició {c.monitoring_started_at}
              </span>
            )}
          </div>

          <Field label="Keywords KPI">
            {(c.kpi_keywords_detail || []).length > 0 ? (
              <div className="space-y-1.5">
                {(c.kpi_keywords_detail || []).map((k) => (
                  <div key={k.kw} className="flex items-start gap-2 py-1.5 border-b border-slate-100 last:border-0" data-testid={`kpi-kw-detail-${k.kw}`}>
                    <span className="text-[11px] font-mono text-slate-400 tabular-nums shrink-0 w-4 mt-0.5">{(c.kpi_keywords_detail.indexOf(k) + 1).toString().padStart(2,'0')}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-[12px] font-medium text-slate-900">{k.kw}</span>
                        {k.type && <span className="text-[9px] uppercase tracking-wider bg-indigo-50 text-indigo-700 border border-indigo-200 px-1.5 py-0.5 rounded-sm font-semibold">{k.type}</span>}
                        {k.funnel && <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm font-semibold border ${k.funnel === 'BOFU' ? 'bg-rose-50 text-rose-700 border-rose-200' : k.funnel === 'MOFU' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-slate-50 text-slate-600 border-slate-200'}`}>{k.funnel}</span>}
                      </div>
                      {k.rationale && <p className="text-[11px] text-slate-500 leading-snug mt-0.5">{k.rationale}</p>}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {(c.kpi_keywords || []).map((k) => (
                  <span key={k} className="text-[11px] bg-indigo-50 text-indigo-700 border border-indigo-200 px-2 py-0.5 rounded-sm font-medium" data-testid={`kpi-kw-${k}`}>{k}</span>
                ))}
              </div>
            )}
          </Field>

          <Field label="Países objetivo">
            <div className="flex flex-wrap gap-1.5">
              {(c.target_countries || []).map((cc) => (
                <span key={cc} className="text-[11px] bg-slate-50 border border-slate-200 px-2 py-0.5 rounded-sm font-medium text-slate-700 inline-flex items-center gap-1">
                  <span>{COUNTRY_FLAGS[cc] || "🌐"}</span> {cc}
                </span>
              ))}
            </div>
          </Field>

          <Field label="Plataformas habilitadas">
            <div className="flex flex-wrap gap-1.5">
              {(c.platforms || []).map((p) => (
                <span key={p} className="text-[11px] bg-slate-50 border border-slate-200 px-2 py-0.5 rounded-sm font-medium text-slate-700 inline-flex items-center gap-1">
                  <Layers className="h-3 w-3" /> {p}
                </span>
              ))}
            </div>
          </Field>

          <div className="grid grid-cols-3 gap-2.5 mt-4 pt-4 border-t border-slate-100">
            <GoalCard label="Salud técnica" value={c.tech_health_goal} suffix="/100" />
            <GoalCard label="Authority Score" value={c.authority_score_goal} />
            <GoalCard label="Visibilidad IA" value={c.ai_visibility_goal} suffix="%" />
          </div>

          {c.notes && (
            <div className="mt-4 pt-4 border-t border-slate-100">
              <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1.5">Notas estratégicas</div>
              <p className="text-xs text-slate-600 leading-relaxed">{c.notes}</p>
            </div>
          )}
        </section>

        {/* Right column placeholder — Day 0 moved to its own tab */}
      </div>

      {/* Justificación de la estrategia */}
      {c.strategy_justification && <StrategyJustificationCard j={c.strategy_justification} />}
    </div>
  );
}

function Day0Section({ data }) {
  const s = data.initial_snapshot || {};
  const dupes = data.evolution?.duplicates_and_forms;
  const blQuality = data.evolution?.backlinks_quality;
  const trafficBreakdown = s.traffic_breakdown || [];
  const aiTraffic = s.ai_traffic_baseline || [];
  const blogFrag = s.blog_fragmentation;
  const kwSnap = s.keywords_kpi_snapshot;
  if (!s.snapshot_date && !s.tech_health) {
    return (
      <div className="bg-white border border-dashed border-slate-300 rounded-md p-10 text-center">
        <Camera className="h-7 w-7 text-slate-400 mx-auto mb-2" />
        <p className="text-sm text-slate-500">Sin snapshot inicial cargado todavía.</p>
      </div>
    );
  }
  return (
    <div className="space-y-6" data-testid="day0-section">
      {/* Snapshot KPIs */}
      <section className="bg-white border border-slate-200 rounded-md p-5">
        <div className="flex items-start gap-2 mb-4 flex-wrap">
          <Camera className="h-4 w-4 text-rose-600 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <h3 className="font-display text-sm font-semibold text-slate-900">Snapshot del día 0</h3>
            <p className="text-[11px] text-slate-500 mt-0.5">Línea de base — la foto inmutable contra la que medimos todo</p>
          </div>
          {s.snapshot_date && (
            <span className="text-[10px] font-mono text-rose-700 bg-rose-50 border border-rose-200 px-2 py-0.5 rounded-sm flex items-center gap-1">
              <Calendar className="h-3 w-3" /> {s.snapshot_date}
            </span>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2.5">
          <SnapStat label="Salud técnica" value={s.tech_health} suffix="/100" tone="indigo" />
          <SnapStat label="Authority Score" value={s.authority_score} tone="violet" />
          <SnapStat label="KW en top 10" value={s.keywords_ranking_top10} suffix={` / ${s.keywords_ranking_total || "—"}`} tone="emerald" />
          <SnapStat label="Tráfico orgánico (est.)" value={s.organic_traffic_estimate} suffix="/mes" tone="sky" />
          <SnapStat label="Backlinks" value={s.backlinks_total} tone="amber" />
          <SnapStat label="Referring domains" value={s.referring_domains} tone="slate" />
        </div>
        <div className="mt-4 pt-4 border-t border-slate-100">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Presencia en IA · Día 0</div>
            {s.ai_readiness_pct != null && (
              <span className="text-[10px] font-mono text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-sm font-semibold inline-flex items-center gap-1" title="Capacidad técnica para ser leída por crawlers de IA">
                AI-ready técnico {s.ai_readiness_pct}%
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5">
            <PresenceChip tool="ChatGPT" present={s.ai_presence_chatgpt} />
            <PresenceChip tool="Perplexity" present={s.ai_presence_perplexity} />
            <PresenceChip tool="Google AIO" present={s.ai_presence_aio} />
          </div>
          {s.ai_presence_note && (
            <p className="text-[11px] text-slate-600 leading-relaxed mt-2 italic">{s.ai_presence_note}</p>
          )}
        </div>
        {(s.highlights || []).length > 0 && (
          <div className="mt-4 pt-4 border-t border-slate-100">
            <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-2">Hallazgos clave</div>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5">
              {s.highlights.map((h, i) => (
                <li key={i} className="text-xs text-slate-600 flex gap-2 items-start">
                  <span className="text-rose-500 mt-0.5">•</span>
                  <span className="leading-relaxed">{h}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {/* Distribución de tráfico */}
      {trafficBreakdown.length > 0 && (
        <div className="grid grid-cols-1 gap-5">
          <TrafficBreakdownCard items={trafficBreakdown} />
        </div>
      )}

      {/* Visibilidad en IA score */}
      {s.ai_visibility_score && <AiVisibilityScoreCard v={s.ai_visibility_score} /> }

      {/* Blog fragmentation */}
      {blogFrag && <BlogFragmentationCard frag={blogFrag} />}

      {/* Keywords KPI snapshot */}
      {kwSnap && <KeywordsKpiSnapshotCard snap={kwSnap} />}

      {/* Absence gap — dónde estamos ausentes vs competidores */}
      {s.absence_gap && <AbsenceGapCard gap={s.absence_gap} />}

      {/* Market growth — GKP validation */}
      {s.market_growth && <MarketGrowthCard mg={s.market_growth} />}

      {/* Calidad de backlinks (moved from Evolución) */}
      {blQuality && <BacklinksQualityCard q={blQuality} />}

      {/* Duplicates + Forms */}
      {dupes && <DuplicatesAndFormsCard d={dupes} />}

      {/* B2B Technical Detail */}
      <TechnicalDetailSection initial={s} />
    </div>
  );
}

function TrafficBreakdownCard({ items }) {
  const palette = ["#6366f1", "#10b981", "#f59e0b", "#0ea5e9", "#8b5cf6", "#f43f5e", "#64748b"];
  return (
    <section className="bg-white border border-slate-200 rounded-md p-5" data-testid="traffic-breakdown-card">
      <div className="flex items-start gap-2 mb-3 flex-wrap">
        <h3 className="font-display text-sm font-semibold text-slate-900">Distribución de tráfico · Día 0</h3>
        <span className="text-[10px] uppercase tracking-[0.16em] text-slate-400 font-semibold">De dónde llegaban las visitas</span>
      </div>
      <div className="space-y-2">
        {items.map((it, i) => {
          const color = palette[i % palette.length];
          return (
            <div key={it.source} className="flex items-center gap-3 flex-wrap" data-testid={`traffic-src-${it.source}`}>
              <div className="w-28 shrink-0 text-xs font-medium text-slate-800">{it.source}</div>
              <div className="flex-1 min-w-[100px]">
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full transition-all" style={{ width: `${it.value}%`, background: color }} />
                </div>
                {it.note && <div className="text-[10px] text-slate-500 mt-1 leading-tight">{it.note}</div>}
              </div>
              <div className="font-mono text-xs font-semibold tabular-nums shrink-0" style={{ color }}>{it.value}{it.unit || "%"}</div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function AiTrafficBaselineCard({ items }) {
  return (
    <section className="bg-white border border-slate-200 rounded-md p-5" data-testid="ai-traffic-card">
      <div className="flex items-start gap-2 mb-3 flex-wrap">
        <Bot className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <h3 className="font-display text-sm font-semibold text-slate-900">Tráfico desde IA · Día 0</h3>
          <p className="text-[11px] text-slate-500 mt-0.5">Sesiones recibidas desde herramientas LLM (últimos 6 meses)</p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2.5">
        {items.map((it) => (
          <div key={it.tool} className={`bg-slate-50 border border-slate-200 rounded-md p-3 ${(it.sessions_6mo || 0) > 0 ? "" : "opacity-70"}`}>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">{it.tool}</div>
            <div className="font-mono text-xl font-semibold text-slate-900 tabular-nums">{it.sessions_6mo || 0}</div>
            <div className="text-[10px] text-slate-500 font-mono mt-0.5">sesiones / 6 meses</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function BlogFragmentationCard({ frag }) {
  const total = (frag.internal_blog_visits || 0) + (frag.external_blog_visits || 0);
  const intPct = total ? Math.round((frag.internal_blog_visits / total) * 100) : 0;
  const extPct = total ? Math.round((frag.external_blog_visits / total) * 100) : 0;
  return (
    <section className="bg-white border border-slate-200 rounded-md p-5" data-testid="blog-fragmentation-card">
      <div className="flex items-start gap-2 mb-3 flex-wrap">
        <FileWarning className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <h3 className="font-display text-sm font-semibold text-slate-900">Blog interno vs externo</h3>
          <p className="text-[11px] text-slate-500 mt-0.5">Fragmentación de autoridad · Día 0</p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3">
          <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold mb-0.5">Blog interno (dominio principal)</div>
          <div className="font-mono text-2xl font-semibold text-emerald-700 tabular-nums">{frag.internal_blog_visits?.toLocaleString()}</div>
          <div className="text-[10px] text-emerald-600 font-mono">{intPct}% del total · consolida autoridad ✓</div>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
          <div className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold mb-0.5">Blog externo (subdominio)</div>
          <div className="font-mono text-2xl font-semibold text-amber-700 tabular-nums">{frag.external_blog_visits?.toLocaleString()}</div>
          <div className="text-[10px] text-amber-600 font-mono">{extPct}% del total · NO consolida ✗</div>
        </div>
      </div>
      {frag.explanation && <p className="text-xs text-slate-600 leading-relaxed">{frag.explanation}</p>}
    </section>
  );
}

function KeywordsKpiSnapshotCard({ snap }) {
  return (
    <section className="bg-white border border-slate-200 rounded-md p-5" data-testid="keywords-snapshot-card">
      <div className="flex items-start gap-2 mb-3 flex-wrap">
        <Target className="h-4 w-4 text-sky-600 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <h3 className="font-display text-sm font-semibold text-slate-900">Keywords KPI · Día 0</h3>
          <p className="text-[11px] text-slate-500 mt-0.5">Estado inicial del posicionamiento orgánico — la foto del problema</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 mb-4">
        <div className="bg-sky-50 border border-sky-200 rounded-md p-3">
          <div className="text-[9px] uppercase tracking-wider text-sky-700 font-semibold mb-0.5">Total KW orgánicas</div>
          <div className="font-mono text-2xl font-semibold text-sky-700 tabular-nums">{snap.total_organic_kw}</div>
        </div>
        <div className="bg-rose-50 border border-rose-200 rounded-md p-3">
          <div className="text-[9px] uppercase tracking-wider text-rose-700 font-semibold mb-0.5">Brandeadas</div>
          <div className="font-mono text-2xl font-semibold text-rose-700 tabular-nums">{snap.branded_pct}<span className="text-xs opacity-70">%</span></div>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3">
          <div className="text-[9px] uppercase tracking-wider text-emerald-700 font-semibold mb-0.5">No-brand (industria)</div>
          <div className="font-mono text-2xl font-semibold text-emerald-700 tabular-nums">{snap.non_branded_pct}<span className="text-xs opacity-70">%</span></div>
        </div>
        <div className="bg-violet-50 border border-violet-200 rounded-md p-3">
          <div className="text-[9px] uppercase tracking-wider text-violet-700 font-semibold mb-0.5">No-brand en top 10</div>
          <div className="font-mono text-2xl font-semibold text-violet-700 tabular-nums">{snap.kw_top_10_total}</div>
        </div>
      </div>

      {snap.explanation && (
        <div className="mb-1 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 flex items-start gap-2">
          <AlertCircle className="h-3.5 w-3.5 text-sky-700 shrink-0 mt-0.5" />
          <p className="text-[11px] text-sky-900 leading-relaxed" dangerouslySetInnerHTML={{ __html: snap.explanation.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>') }} />
        </div>
      )}
    </section>
  );
}

function StrategyJustificationCard({ j }) {
  const toneMap = {
    aspirational: "bg-rose-50 border-rose-200 text-rose-700",
    baseline: "bg-indigo-50 border-indigo-300 text-indigo-800 font-semibold",
    beaten: "bg-emerald-50 border-emerald-200 text-emerald-700",
  };
  return (
    <section className="bg-white border border-slate-200 rounded-md p-5 space-y-5" data-testid="strategy-justification-card">
      <div className="flex items-start gap-2 flex-wrap">
        <Sparkles className="h-4 w-4 text-indigo-600 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <h3 className="font-display text-sm font-semibold text-slate-900">Justificación de la estrategia</h3>
          <p className="text-[11px] text-slate-500 mt-0.5">Por qué estas keywords y por qué son captureables ahora</p>
        </div>
      </div>

      {j.headline && (
        <div className="rounded-md bg-indigo-50 border border-indigo-200 px-4 py-3">
          <p className="text-[13px] text-indigo-900 leading-relaxed font-medium">{j.headline}</p>
        </div>
      )}
      {j.context && <p className="text-xs text-slate-600 leading-relaxed">{j.context}</p>}

      {(j.criteria || []).length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-2">Criterios de selección</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
            {j.criteria.map((c, i) => (
              <div key={i} className="rounded-md border border-slate-200 bg-slate-50 p-3" data-testid={`strategy-criterion-${i}`}>
                <div className="text-[11px] font-semibold text-slate-900 mb-1">{c.title}</div>
                <p className="text-[11px] text-slate-600 leading-snug">{c.detail}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {(j.authority_scores || []).length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-2">Authority Score comparativo · Semrush jun. 2026</div>
          <div className="overflow-x-auto -mx-1">
            <table className="w-full text-[11px] table-fixed">
              <thead className="bg-slate-50 border-y border-slate-200">
                <tr className="text-left text-slate-500 uppercase tracking-wider text-[9px]">
                  <th className="px-3 py-2 font-semibold w-2/5">Dominio</th>
                  <th className="px-3 py-2 font-semibold text-right w-16">AS</th>
                  <th className="px-3 py-2 font-semibold text-right w-24">Ref. Dom.</th>
                  <th className="px-3 py-2 font-semibold text-right w-24">Δ vs For_Me</th>
                  <th className="px-3 py-2 font-semibold">Lectura</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {j.authority_scores.map((a, i) => (
                  <tr key={i} className={`hover:bg-slate-50 ${a.tone === 'baseline' ? 'bg-indigo-50/40' : ''}`} data-testid={`as-row-${i}`}>
                    <td className="px-3 py-2 font-mono text-[11px] text-slate-800 truncate" title={a.domain}>{a.domain}</td>
                    <td className="px-3 py-2 font-mono text-[12px] font-semibold tabular-nums text-right text-slate-900">{a.as_score}</td>
                    <td className="px-3 py-2 font-mono text-[11px] tabular-nums text-right text-slate-600">{(a.ref_domains || 0).toLocaleString()}</td>
                    <td className={`px-3 py-2 font-mono text-[11px] font-semibold tabular-nums text-right ${a.delta_vs_forme > 0 ? 'text-emerald-700' : a.delta_vs_forme < 0 ? 'text-rose-700' : 'text-slate-500'}`}>
                      {a.delta_vs_forme > 0 ? `+${a.delta_vs_forme}` : a.delta_vs_forme}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`text-[9.5px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm font-semibold border ${toneMap[a.tone] || 'bg-slate-50 border-slate-200 text-slate-600'}`}>{a.label}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {(j.competitor_map || []).length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-2">Mapa competitivo · quién compite y desde qué ángulo</div>
          <div className="space-y-1.5">
            {j.competitor_map.map((cm, i) => (
              <div key={i} className="rounded-md border border-slate-200 bg-white p-3" data-testid={`competitor-cat-${i}`}>
                <div className="flex items-start gap-2 flex-wrap mb-1">
                  <span className="text-[12px] font-semibold text-slate-900">{cm.category}</span>
                  {(cm.examples || []).map((e) => (
                    <span key={e} className="text-[10px] bg-slate-50 border border-slate-200 px-1.5 py-0.5 rounded-sm font-mono text-slate-600">{e}</span>
                  ))}
                </div>
                <p className="text-[11px] text-slate-500 leading-snug"><span className="text-slate-700 font-medium">Modelo:</span> {cm.model}</p>
                <p className="text-[11px] text-slate-500 leading-snug mt-0.5"><span className="text-slate-700 font-medium">Relación con For_Me:</span> {cm.relation}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {j.closing_note && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3">
          <p className="text-[12px] text-emerald-900 leading-relaxed italic">{j.closing_note}</p>
        </div>
      )}
    </section>
  );
}

function AbsenceGapCard({ gap }) {
  return (
    <section className="bg-white border border-rose-200 rounded-md p-5" data-testid="absence-gap-card">
      <div className="flex items-start gap-2 mb-3 flex-wrap">
        <FileWarning className="h-4 w-4 text-rose-600 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <h3 className="font-display text-sm font-semibold text-slate-900">{gap.title || "Ausencia en términos core"}</h3>
          {gap.subtitle && <p className="text-[11px] text-slate-500 mt-0.5">{gap.subtitle}</p>}
        </div>
        {gap.source && (
          <span className="text-[10px] font-mono text-slate-500 bg-slate-50 border border-slate-200 px-2 py-0.5 rounded-sm">{gap.source}</span>
        )}
      </div>

      <div className="overflow-x-auto -mx-1">
        <table className="w-full text-[11px] table-fixed">
          <colgroup>
            <col className="w-[28%]" />
            <col className="w-[15%]" />
            <col className="w-[14%]" />
            <col className="w-[25%]" />
            <col className="w-[18%]" />
          </colgroup>
          <thead className="bg-slate-50 border-y border-slate-200">
            <tr className="text-left text-slate-500 uppercase tracking-wider text-[9px]">
              <th className="px-3 py-2 font-semibold">Keyword</th>
              <th className="px-3 py-2 font-semibold text-right">Vol./mes</th>
              <th className="px-3 py-2 font-semibold">For_Me</th>
              <th className="px-3 py-2 font-semibold">Competidores</th>
              <th className="px-3 py-2 font-semibold">Situación</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(gap.items || []).map((it, i) => (
              <tr key={i} className="hover:bg-slate-50" data-testid={`absence-row-${it.kw}`}>
                <td className="px-3 py-2 font-medium text-slate-800 truncate" title={it.kw}>{it.kw}</td>
                <td className="px-3 py-2 font-mono text-[11.5px] font-semibold tabular-nums text-right text-slate-900">{(it.volume || 0).toLocaleString()}</td>
                <td className="px-3 py-2">
                  <span className="text-[9.5px] uppercase tracking-wider bg-rose-50 text-rose-700 border border-rose-200 px-1.5 py-0.5 rounded-sm font-semibold">✕ {it.forme_status}</span>
                </td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1">
                    {(it.competitors || []).map((cp) => (
                      <span key={cp.name} className="text-[10px] font-mono bg-slate-50 border border-slate-200 px-1.5 py-0.5 rounded-sm text-slate-700">
                        {cp.name} <span className="text-slate-400">·</span> <span className="text-slate-500">pos {cp.position}</span>
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-3 py-2 text-[10.5px] text-slate-500 leading-snug italic">{it.note}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function MarketGrowthCard({ mg }) {
  return (
    <section className="bg-white border border-emerald-200 rounded-md p-5" data-testid="market-growth-card">
      <div className="flex items-start gap-2 mb-3 flex-wrap">
        <ArrowUpRight className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <h3 className="font-display text-sm font-semibold text-slate-900">{mg.title || "Datos de mercado"}</h3>
          {mg.subtitle && <p className="text-[11px] text-slate-500 mt-0.5">{mg.subtitle}</p>}
        </div>
        {mg.source && (
          <span className="text-[10px] font-mono text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-sm">{mg.source}</span>
        )}
      </div>

      <div className="overflow-x-auto -mx-1">
        <table className="w-full text-[11px] table-fixed">
          <colgroup>
            <col className="w-[26%]" />
            <col className="w-[13%]" />
            <col className="w-[13%]" />
            <col className="w-[15%]" />
            <col className="w-[33%]" />
          </colgroup>
          <thead className="bg-slate-50 border-y border-slate-200">
            <tr className="text-left text-slate-500 uppercase tracking-wider text-[9px]">
              <th className="px-3 py-2 font-semibold">Keyword</th>
              <th className="px-3 py-2 font-semibold text-right">Vol./mes</th>
              <th className="px-3 py-2 font-semibold text-right">Δ 3 meses</th>
              <th className="px-3 py-2 font-semibold text-right">Δ interanual</th>
              <th className="px-3 py-2 font-semibold">Lectura estratégica</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(mg.items || []).map((it, i) => (
              <tr key={i} className="hover:bg-slate-50" data-testid={`market-growth-row-${it.kw}`}>
                <td className="px-3 py-2 font-medium text-slate-800 truncate" title={it.kw}>{it.kw}</td>
                <td className="px-3 py-2 font-mono text-[11px] tabular-nums text-right text-slate-800">{(it.vol_month || 0).toLocaleString()}</td>
                <td className={`px-3 py-2 font-mono text-[11px] font-semibold tabular-nums text-right ${it.delta_3m_pct > 0 ? 'text-emerald-700' : it.delta_3m_pct < 0 ? 'text-rose-700' : 'text-slate-500'}`}>
                  {it.delta_3m_pct > 0 ? `+${it.delta_3m_pct}%` : `${it.delta_3m_pct}%`}
                </td>
                <td className="px-3 py-2 font-mono text-[11px] font-semibold tabular-nums text-right text-emerald-700">{it.delta_yoy_label}</td>
                <td className="px-3 py-2 text-[10.5px] text-slate-500 leading-snug">{it.reading}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {mg.note && (
        <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
          <p className="text-[10.5px] text-slate-600 leading-snug italic">{mg.note}</p>
        </div>
      )}
    </section>
  );
}

function DuplicatesAndFormsCard({ d }) {
  const hasLostStats = d.lost_referring_domains_count > 0 || d.active_referring_domains > 0;
  const biggest = d.biggest_drop || d.biggest_monthly_drop;
  return (
    <section className="bg-white border border-slate-200 rounded-md p-5" data-testid="duplicates-forms-card">
      <div className="flex items-start gap-2 mb-3 flex-wrap">
        <FileWarning className="h-4 w-4 text-rose-600 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <h3 className="font-display text-sm font-semibold text-slate-900">{d.title || "Pérdida de dominios referentes + URLs duplicadas"}</h3>
          <p className="text-[11px] text-slate-500 mt-0.5">Causa raíz de la caída de backlinks · Día 0</p>
        </div>
        <span className="text-[10px] font-mono text-rose-700 bg-rose-50 border border-rose-200 px-2 py-0.5 rounded-sm flex items-center gap-1">Día 0</span>
      </div>

      {hasLostStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 mb-4">
          <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3">
            <div className="text-[9px] uppercase tracking-wider text-emerald-700 font-semibold mb-0.5">RD activos hoy</div>
            <div className="font-mono text-xl font-semibold text-emerald-700 tabular-nums">{d.active_referring_domains}</div>
          </div>
          <div className="bg-rose-50 border border-rose-200 rounded-md p-3">
            <div className="text-[9px] uppercase tracking-wider text-rose-700 font-semibold mb-0.5">RD perdidos</div>
            <div className="font-mono text-xl font-semibold text-rose-700 tabular-nums">-{d.lost_referring_domains_count}</div>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
            <div className="text-[9px] uppercase tracking-wider text-slate-600 font-semibold mb-0.5">Pico histórico</div>
            <div className="font-mono text-xl font-semibold text-slate-700 tabular-nums">{d.peak_referring_domains}</div>
          </div>
          {biggest && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
              <div className="text-[9px] uppercase tracking-wider text-amber-700 font-semibold mb-0.5">Mayor caída</div>
              <div className="font-mono text-xl font-semibold text-amber-700 tabular-nums">{biggest.delta}</div>
              <div className="text-[9px] text-amber-600 font-mono mt-0.5">{biggest.type}</div>
            </div>
          )}
        </div>
      )}

      {d.summary && (
        <p className="text-xs text-slate-600 leading-relaxed mb-4" dangerouslySetInnerHTML={{ __html: d.summary.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>') }} />
      )}

      <div className="space-y-2">
        {(d.items || []).map((it, i) => (
          <div key={i} className="bg-rose-50/40 border border-rose-200 rounded-md p-3 flex items-center gap-3 flex-wrap" data-testid={`dup-item-${i}`}>
            <LinkIcon className="h-3.5 w-3.5 text-rose-600 shrink-0" />
            <div className="flex-1 min-w-[180px]">
              <div className="text-xs font-medium text-slate-800">{it.label}</div>
              <div className="text-[10px] text-slate-500 mt-0.5">Fix: {it.fix}</div>
            </div>
            <div className="text-right">
              <div className="font-mono text-sm font-semibold text-rose-700 tabular-nums">{typeof it.backlinks_in_limbo === "number" ? it.backlinks_in_limbo.toLocaleString() : it.backlinks_in_limbo}</div>
              <div className="text-[9px] uppercase tracking-wider text-rose-500 font-semibold">BL en limbo</div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function TechnicalDetailSection({ initial }) {
  const themes = initial?.thematic_scores || [];
  const issues = initial?.issues_breakdown || [];
  const projections = initial?.projections || [];
  if (themes.length === 0 && issues.length === 0 && projections.length === 0) return null;

  return (
    <div className="space-y-5" data-testid="technical-detail-section">
      {(themes.length > 0 || issues.length > 0) && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
          {themes.length > 0 && <ThematicScoresCard themes={themes} />}
          {issues.length > 0 && <IssuesCard issues={issues} />}
        </div>
      )}
      {projections.length > 0 && <ProjectionsCard projections={projections} />}
    </div>
  );
}

function ThematicScoresCard({ themes }) {
  const statusTone = {
    ok: { dot: "bg-emerald-500", text: "text-emerald-700", label: "Óptimo" },
    warning: { dot: "bg-amber-500", text: "text-amber-700", label: "Mejorable" },
    regular: { dot: "bg-slate-400", text: "text-slate-600", label: "Regular" },
    error: { dot: "bg-rose-500", text: "text-rose-700", label: "Crítico" },
  };
  const priorityTone = {
    high: "bg-rose-50 text-rose-700 border-rose-200",
    medium: "bg-amber-50 text-amber-700 border-amber-200",
    low: "bg-slate-50 text-slate-600 border-slate-200",
    ALTA: "bg-rose-50 text-rose-700 border-rose-200",
    MEDIA: "bg-amber-50 text-amber-700 border-amber-200",
    BAJA: "bg-slate-50 text-slate-600 border-slate-200",
  };
  return (
    <section className="bg-white border border-slate-200 rounded-md p-5" data-testid="thematic-scores-card">
      <div className="flex items-start gap-2 mb-4 flex-wrap">
        <h3 className="font-display text-sm font-semibold text-slate-900">Scores temáticos · Auditoría técnica</h3>
        <span className="text-[10px] uppercase tracking-[0.16em] text-slate-400 font-semibold">Por dimensión SEO</span>
      </div>
      <div className="overflow-x-auto -mx-5">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 border-y border-slate-200">
            <tr className="text-left text-slate-500 uppercase tracking-wider text-[10px]">
              <th className="px-3 py-2 font-semibold">Dimensión</th>
              <th className="px-2 py-2 font-semibold text-right">Score</th>
              <th className="px-2 py-2 font-semibold">Estado</th>
              <th className="px-2 py-2 font-semibold">Prioridad</th>
              <th className="px-2 py-2 font-semibold">Observación</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {themes.map((t) => {
              const tone = statusTone[t.status] || statusTone.regular;
              const isGlobal = t.dimension?.toLowerCase().includes("global");
              return (
                <tr key={t.dimension} className={`hover:bg-slate-50 ${isGlobal ? "bg-indigo-50/40 font-semibold" : ""}`} data-testid={`theme-${t.dimension}`}>
                  <td className="px-3 py-2 text-slate-800">{t.dimension}</td>
                  <td className="px-2 py-2 text-right">
                    <span className={`font-mono text-sm font-semibold tabular-nums ${t.score >= 90 ? "text-emerald-700" : t.score >= 75 ? "text-amber-700" : "text-rose-700"}`}>{t.score}</span>
                    <span className="text-[10px] text-slate-400 ml-0.5">/100</span>
                  </td>
                  <td className="px-2 py-2">
                    <span className={`inline-flex items-center gap-1 text-[10px] font-medium ${tone.text}`}>
                      <span className={`h-1.5 w-1.5 rounded-full ${tone.dot}`} />{tone.label}
                    </span>
                  </td>
                  <td className="px-2 py-2">
                    {t.priority && t.priority !== "—" && (
                      <span className={`text-[10px] font-mono uppercase tracking-wider border px-1.5 py-0.5 rounded-sm font-semibold ${priorityTone[t.priority] || priorityTone.low}`}>{t.priority}</span>
                    )}
                  </td>
                  <td className="px-2 py-2 text-slate-600 text-[11px]">{t.observation}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function IssuesCard({ issues }) {
  const typeTone = {
    error: { bg: "bg-rose-50 border-rose-200", text: "text-rose-700", value: "text-rose-700" },
    warning: { bg: "bg-amber-50 border-amber-200", text: "text-amber-700", value: "text-amber-700" },
    notice: { bg: "bg-sky-50 border-sky-200", text: "text-sky-700", value: "text-sky-700" },
    info: { bg: "bg-slate-50 border-slate-200", text: "text-slate-600", value: "text-slate-700" },
    crawl: { bg: "bg-slate-50 border-slate-200", text: "text-slate-600", value: "text-slate-700" },
  };
  return (
    <section className="bg-white border border-slate-200 rounded-md p-5" data-testid="issues-card">
      <div className="flex items-start gap-2 mb-4 flex-wrap">
        <h3 className="font-display text-sm font-semibold text-slate-900">Issues detectados</h3>
        <span className="text-[10px] uppercase tracking-[0.16em] text-slate-400 font-semibold">Resumen del crawl</span>
      </div>
      <div className="space-y-2">
        {issues.map((i) => {
          const tone = typeTone[i.type] || typeTone.info;
          return (
            <div key={i.indicator} className={`${tone.bg} border rounded-md px-3 py-2 flex items-center gap-3 flex-wrap`} data-testid={`issue-${i.indicator}`}>
              <div className="flex-1 min-w-[160px]">
                <div className={`text-[11px] font-semibold ${tone.text}`}>{i.indicator}</div>
                <div className="text-[10px] text-slate-500 mt-0.5">{i.description}</div>
              </div>
              <div className={`font-mono text-base font-semibold tabular-nums ${tone.value}`}>{i.value}</div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function ProjectionsCard({ projections }) {
  return (
    <section className="bg-white border border-slate-200 rounded-md p-5" data-testid="projections-card">
      <div className="flex items-start gap-2 mb-4 flex-wrap">
        <h3 className="font-display text-sm font-semibold text-slate-900">KPIs técnicos · proyección 30 / 60 / 90 días</h3>
        <span className="text-[10px] uppercase tracking-[0.16em] text-slate-400 font-semibold">Comprometido al cliente</span>
      </div>
      <div className="overflow-x-auto -mx-5">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 border-y border-slate-200">
            <tr className="text-left text-slate-500 uppercase tracking-wider text-[10px]">
              <th className="px-3 py-2 font-semibold w-2/5">KPI</th>
              <th className="px-2 py-2 font-semibold text-right">Valor actual</th>
              <th className="px-2 py-2 font-semibold text-right">30 días</th>
              <th className="px-2 py-2 font-semibold text-right">60 días</th>
              <th className="px-2 py-2 font-semibold text-right">90 días</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {projections.map((p) => (
              <tr key={p.kpi} className="hover:bg-slate-50" data-testid={`projection-${p.kpi}`}>
                <td className="px-3 py-2 font-medium text-slate-800">{p.kpi}</td>
                <td className="px-2 py-2 text-right font-mono tabular-nums text-slate-500">{p.current}</td>
                <td className="px-2 py-2 text-right font-mono tabular-nums text-sky-700 font-semibold">{p.d30}</td>
                <td className="px-2 py-2 text-right font-mono tabular-nums text-amber-700 font-semibold">{p.d60}</td>
                <td className="px-2 py-2 text-right font-mono tabular-nums text-emerald-700 font-semibold">{p.d90}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div className="mb-3.5">
      <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1.5">{label}</div>
      {children}
    </div>
  );
}

function DeliverableTile({ label, value, suffix, tone = "slate", isBool }) {
  const tones = {
    indigo: { bg: "bg-indigo-50", border: "border-indigo-200", text: "text-indigo-700", icon: "text-indigo-500" },
    sky: { bg: "bg-sky-50", border: "border-sky-200", text: "text-sky-700", icon: "text-sky-500" },
    emerald: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", icon: "text-emerald-500" },
    violet: { bg: "bg-violet-50", border: "border-violet-200", text: "text-violet-700", icon: "text-violet-500" },
    amber: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", icon: "text-amber-500" },
    rose: { bg: "bg-rose-50", border: "border-rose-200", text: "text-rose-700", icon: "text-rose-500" },
    slate: { bg: "bg-slate-50", border: "border-slate-200", text: "text-slate-700", icon: "text-slate-500" },
  };
  const t = tones[tone] || tones.slate;
  return (
    <div className={`${t.bg} ${t.border} border rounded-md p-3`}>
      <div className={`text-[10px] uppercase tracking-wider font-semibold mb-1.5 ${t.text} leading-tight`}>{label}</div>
      <div className={`font-mono font-semibold tabular-nums ${t.text} ${isBool ? "text-xl" : "text-2xl"} flex items-baseline gap-1`}>
        {value}
        <span className="text-[10px] opacity-70 font-normal uppercase tracking-wider">{suffix}</span>
      </div>
    </div>
  );
}

function GoalCard({ label, value, suffix = "" }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-md p-2.5">
      <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold mb-1">{label}</div>
      <div className="font-mono text-lg font-semibold text-slate-900 tabular-nums">{value ?? "—"}<span className="text-xs text-slate-500 ml-0.5">{suffix}</span></div>
    </div>
  );
}

function SnapStat({ label, value, suffix = "", tone = "slate" }) {
  const tones = {
    indigo: "text-indigo-700 bg-indigo-50 border-indigo-200",
    violet: "text-violet-700 bg-violet-50 border-violet-200",
    emerald: "text-emerald-700 bg-emerald-50 border-emerald-200",
    sky: "text-sky-700 bg-sky-50 border-sky-200",
    amber: "text-amber-700 bg-amber-50 border-amber-200",
    slate: "text-slate-700 bg-slate-50 border-slate-200",
  };
  return (
    <div className={`border rounded-md p-3 ${tones[tone]}`}>
      <div className="text-[9px] uppercase tracking-wider opacity-80 font-semibold mb-1">{label}</div>
      <div className="font-mono text-xl font-semibold tabular-nums">{value ?? "—"}<span className="text-[10px] opacity-70 ml-0.5">{suffix}</span></div>
    </div>
  );
}

function PresenceChip({ tool, present }) {
  const tone = TOOL_TONE[tool] || TOOL_TONE.ChatGPT;
  // Backwards compatible: bool True/False, or string "sporadic"/"absent"/"present"
  const state = present === true || present === "present"
    ? "present"
    : present === "sporadic"
    ? "sporadic"
    : "absent";
  const cfg = {
    present: { cls: `${tone.bg} ${tone.border} ${tone.text}`, label: "SÍ" },
    sporadic: { cls: "bg-amber-50 border-amber-200 text-amber-700", label: "Esporádica" },
    absent: { cls: "bg-slate-50 border-slate-200 text-slate-500", label: "—" },
  }[state];
  return (
    <span className={`text-[11px] border px-2 py-0.5 rounded-sm font-medium inline-flex items-center gap-1.5 ${cfg.cls}`}>
      <Bot className="h-3 w-3" />
      {tool}
      <span className="font-mono text-[9px]">{cfg.label}</span>
    </span>
  );
}

/* ============== Evolución orgánica ============== */
function EvolutionSection({ clientId, data, initial, compromiso }) {
  const traffic = data?.traffic || [];
  const techHealth = data?.tech_health || [];
  const backlinks = data?.backlinks || [];
  const refDomains = data?.referring_domains || [];
  const authority = data?.authority_score || [];
  const kwRows = data?.keyword_positions || [];
  const kwNote = data?.keywords_note;
  const blQuality = data?.backlinks_quality;

  const lastTraffic = traffic[traffic.length - 1]?.value;
  const firstTraffic = traffic[0]?.value;
  const trafficDelta = lastTraffic && firstTraffic ? lastTraffic - firstTraffic : null;

  // Group by keyword for table
  const kwGrouped = useMemo(() => {
    const map = {};
    for (const r of kwRows) {
      if (!map[r.kw]) map[r.kw] = [];
      map[r.kw].push(r);
    }
    return Object.entries(map).map(([kw, points]) => {
      const sorted = [...points].sort((a, b) => (a.date > b.date ? 1 : -1));
      const first = sorted[0];
      const last = sorted[sorted.length - 1];
      return {
        kw,
        first_date: first?.date,
        first_pos: first?.position,
        last_date: last?.date,
        last_pos: last?.position,
        delta: first?.position != null && last?.position != null ? first.position - last.position : 0, // positive = went UP in ranking
        history: sorted,
      };
    }).sort((a, b) => (a.last_pos || 999) - (b.last_pos || 999));
  }, [kwRows]);

  return (
    <div className="space-y-6" data-testid="evolution-section">
      {/* Integration pending notice */}
      <div className="bg-amber-50 border border-amber-200 rounded-md p-4 flex items-start gap-3" data-testid="evolution-pending-notice">
        <AlertCircle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold text-amber-900 mb-0.5">Integración con SEMrush / Google Analytics pendiente</div>
          <p className="text-xs text-amber-800 leading-relaxed">
            Esta vista todavía no está conectada a las APIs reales. Los gráficos parten del <span className="font-semibold">Snap de día cero</span> que cargaste manualmente y muestran datos simulados hasta que activemos la integración. Apenas la conectemos, todo se actualiza solo.
          </p>
        </div>
      </div>

      {/* Summary KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <EvolveKpi label="Tráfico orgánico" value={lastTraffic} initial={firstTraffic} suffix="/mes" tone="sky" higherIsBetter />
        <EvolveKpi label="Salud técnica" value={techHealth[techHealth.length - 1]?.value} initial={techHealth[0]?.value} suffix="/100" tone="indigo" higherIsBetter goal={compromiso?.tech_health_goal} />
        <EvolveKpi label="Authority Score" value={authority[authority.length - 1]?.value} initial={authority[0]?.value} tone="violet" higherIsBetter goal={compromiso?.authority_score_goal} />
        <EvolveKpi label="Backlinks" value={backlinks[backlinks.length - 1]?.value} initial={backlinks[0]?.value} tone="amber" higherIsBetter />
        <EvolveKpi label="Referring domains" value={refDomains[refDomains.length - 1]?.value} initial={refDomains[0]?.value} tone="rose" higherIsBetter />
      </div>

      {/* Traffic trend chart */}
      <section className="bg-white border border-slate-200 rounded-md p-5">
        <div className="flex items-start gap-2 mb-4 flex-wrap">
          <h3 className="font-display text-sm font-semibold text-slate-900">Tráfico orgánico estimado</h3>
          <span className="text-[10px] uppercase tracking-[0.16em] text-slate-400 font-semibold">Visitas/mes · serie semanal</span>
          {trafficDelta != null && (
            <span className={`ml-auto text-[10px] font-mono font-semibold px-2 py-0.5 rounded-sm border ${trafficDelta >= 0 ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-rose-50 text-rose-700 border-rose-200"}`}>
              {trafficDelta >= 0 ? "+" : ""}{trafficDelta.toLocaleString()} vs Día 0
            </span>
          )}
        </div>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={traffic} margin={{ top: 6, right: 12, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="trafGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0ea5e9" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#0ea5e9" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#64748b" }} />
              <YAxis tick={{ fontSize: 10, fill: "#64748b" }} />
              <Tooltip contentStyle={{ fontSize: 11, borderRadius: 6 }} />
              <Area type="monotone" dataKey="value" stroke="#0ea5e9" strokeWidth={2} fill="url(#trafGrad)" name="Visitas/mes" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* Two-up: Tech health + Authority */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <MiniLineChart title="Salud técnica" subtitle="Score Site Audit · evolución" data={techHealth} color="#6366f1" />
        <MiniLineChart title="Authority Score" subtitle="Domain authority" data={authority} color="#8b5cf6" />
      </div>

      {/* Backlinks + Referring evolution charts (without quality card — that moved to Día 0) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <MiniLineChart title="Backlinks totales" subtitle="Pico vs hoy · serie semanal" data={backlinks} color="#f59e0b" />
        <MiniLineChart title="Referring domains" subtitle="Diversidad de fuentes · serie semanal" data={refDomains} color="#f43f5e" />
      </div>

      {/* Keyword rankings tracking (manual or via SEMrush integration) */}
      <KeywordRankingsCard
        clientId={clientId}
        rows={kwGrouped}
        kwList={compromiso?.kpi_keywords || []}
        snapshotDate={initial?.snapshot_date || compromiso?.monitoring_started_at}
        gscSiteUrl={compromiso?.gsc_site_url || ""}
      />

      {/* Top queries reales · Search Console */}
      <GscTopQueriesCard defaultSiteUrl={compromiso?.gsc_site_url || ""} />
    </div>
  );
}

function EvolveKpi({ label, value, initial, suffix = "", tone = "slate", higherIsBetter = true, goal }) {
  const delta = value != null && initial != null ? value - initial : null;
  const isPositive = higherIsBetter ? (delta || 0) >= 0 : (delta || 0) <= 0;
  const tones = {
    indigo: "from-indigo-50 to-white border-indigo-200 text-indigo-700",
    violet: "from-violet-50 to-white border-violet-200 text-violet-700",
    sky: "from-sky-50 to-white border-sky-200 text-sky-700",
    amber: "from-amber-50 to-white border-amber-200 text-amber-700",
    slate: "from-slate-50 to-white border-slate-200 text-slate-700",
  };
  return (
    <div className={`bg-gradient-to-b ${tones[tone]} border rounded-md p-4`}>
      <div className="text-[9px] uppercase tracking-wider opacity-90 font-semibold mb-1">{label}</div>
      <div className="font-mono text-2xl font-semibold tabular-nums">{value ?? "—"}<span className="text-[11px] opacity-70 ml-0.5">{suffix}</span></div>
      <div className="flex items-center gap-2 mt-2">
        {delta != null && (
          <span className={`text-[10px] font-mono font-semibold inline-flex items-center gap-0.5 ${isPositive ? "text-emerald-700" : "text-rose-700"}`}>
            {isPositive ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
            {delta >= 0 ? "+" : ""}{delta} vs Día 0
          </span>
        )}
        {goal != null && (
          <span className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold ml-auto">Goal {goal}{suffix}</span>
        )}
      </div>
    </div>
  );
}

function MiniLineChart({ title, subtitle, data, color }) {
  const last = data[data.length - 1]?.value;
  const first = data[0]?.value;
  const delta = last != null && first != null ? last - first : null;
  return (
    <section className="bg-white border border-slate-200 rounded-md p-5">
      <div className="flex items-start gap-2 mb-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <h3 className="font-display text-sm font-semibold text-slate-900">{title}</h3>
          <p className="text-[11px] text-slate-500 mt-0.5">{subtitle}</p>
        </div>
        <div className="text-right">
          <div className="font-mono text-xl font-semibold text-slate-900 tabular-nums">{last ?? "—"}</div>
          {delta != null && (
            <div className={`text-[10px] font-mono font-semibold ${delta >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
              {delta >= 0 ? "+" : ""}{delta} vs inicio
            </div>
          )}
        </div>
      </div>
      <div className="h-32">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 6, right: 4, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="date" tick={{ fontSize: 9, fill: "#64748b" }} />
            <YAxis tick={{ fontSize: 9, fill: "#64748b" }} />
            <Tooltip contentStyle={{ fontSize: 11, borderRadius: 6 }} />
            <Line type="monotone" dataKey="value" stroke={color} strokeWidth={2} dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}

function AiVisibilityScoreCard({ v }) {
  const [llmTab, setLlmTab] = useState("mentions");
  const score = v.score || 0;
  const max = v.max || 100;
  const pct = (score / max) * 100;
  const labelTone = score >= 60 ? "text-emerald-700 bg-emerald-50 border-emerald-200" : score >= 30 ? "text-amber-700 bg-amber-50 border-amber-200" : "text-rose-700 bg-rose-50 border-rose-200";
  const ringColor = score >= 60 ? "#10b981" : score >= 30 ? "#f59e0b" : "#f97316";
  const r = 38;
  const C = 2 * Math.PI * r;
  const fmtDelta = (d) => d == null ? null : `${d > 0 ? "+" : ""}${d}%`;
  const deltaCls = (d) => d > 0 ? "text-emerald-600" : d < 0 ? "text-rose-600" : "text-slate-500";
  return (
    <section className="bg-white border border-slate-200 rounded-md p-5" data-testid="ai-visibility-score-card">
      <div className="flex items-start gap-2 mb-4 flex-wrap">
        <Sparkles className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <h3 className="font-display text-sm font-semibold text-slate-900">Visibilidad en IA · Día 0</h3>
          <p className="text-[11px] text-slate-500 mt-0.5">Frecuencia de aparición de la marca en respuestas de LLM vs competidoras</p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 items-center">
        <div className="flex items-center gap-3">
          <svg width="100" height="100" viewBox="0 0 100 100" className="shrink-0">
            <circle cx="50" cy="50" r={r} fill="none" stroke="#e2e8f0" strokeWidth="10" />
            <circle cx="50" cy="50" r={r} fill="none" stroke={ringColor} strokeWidth="10" strokeLinecap="round"
              strokeDasharray={C} strokeDashoffset={C - (pct / 100) * C} transform="rotate(-90 50 50)" />
            <text x="50" y="48" textAnchor="middle" className="fill-slate-900" fontSize="20" fontWeight="700" fontFamily="ui-monospace,monospace">{score}</text>
            <text x="50" y="62" textAnchor="middle" className="fill-slate-400" fontSize="9">/ {max}</text>
          </svg>
          <div>
            <span className={`inline-block text-[11px] font-semibold border px-2 py-0.5 rounded-sm ${labelTone}`}>{v.label}</span>
            <p className="text-[11px] text-slate-600 leading-relaxed mt-2">{v.description}</p>
          </div>
        </div>
        <div className="grid grid-cols-3 md:col-span-2 gap-2">
          <div className="bg-slate-50 border border-slate-200 rounded-md p-2.5">
            <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold mb-0.5">Menciones</div>
            <div className="font-mono text-xl font-semibold text-slate-900 tabular-nums flex items-baseline gap-1">
              {v.mentions}
              {v.mentions_delta_pct != null && <span className={`text-[10px] font-semibold ${deltaCls(v.mentions_delta_pct)}`}>{fmtDelta(v.mentions_delta_pct)}</span>}
            </div>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-md p-2.5">
            <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold mb-0.5">Citas</div>
            <div className="font-mono text-xl font-semibold text-slate-900 tabular-nums flex items-baseline gap-1">
              {v.citations}
              {v.citations_delta_pct != null && <span className={`text-[10px] font-semibold ${deltaCls(v.citations_delta_pct)}`}>{fmtDelta(v.citations_delta_pct)}</span>}
            </div>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-md p-2.5">
            <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold mb-0.5">Pág. citadas</div>
            <div className="font-mono text-xl font-semibold text-slate-900 tabular-nums">{v.cited_pages}</div>
          </div>
        </div>
      </div>
      {v.context_note && (
        <div className="mt-4 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 flex items-start gap-2">
          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-700 shrink-0 mt-0.5" />
          <p className="text-[11px] text-emerald-900 leading-relaxed">{v.context_note}</p>
        </div>
      )}
      <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-1 md:grid-cols-2 gap-5">
        {(v.llm_distribution || []).length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Distribución por LLM</div>
              {(v.llm_distribution_pages || []).length > 0 && (
                <div className="inline-flex bg-slate-100 border border-slate-200 rounded-md p-0.5">
                  <button onClick={() => setLlmTab("mentions")} data-testid="llm-tab-mentions"
                    className={`px-2 py-0.5 text-[10px] font-medium rounded-sm ${llmTab === "mentions" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600"}`}>Menciones</button>
                  <button onClick={() => setLlmTab("pages")} data-testid="llm-tab-pages"
                    className={`px-2 py-0.5 text-[10px] font-medium rounded-sm ${llmTab === "pages" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600"}`}>Páginas citadas</button>
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              {(llmTab === "pages" && v.llm_distribution_pages ? v.llm_distribution_pages : v.llm_distribution).map((l) => (
                <div key={l.llm} className="flex items-center gap-3 flex-wrap">
                  <div className="w-32 shrink-0 text-xs font-medium text-slate-800">{l.llm}</div>
                  <div className="flex-1 min-w-[80px]">
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-violet-500 transition-all" style={{ width: `${l.pct}%` }} />
                    </div>
                  </div>
                  <div className="font-mono text-[11px] font-semibold text-violet-700 tabular-nums shrink-0">{l.pct}%</div>
                  <div className="font-mono text-[10px] text-slate-500 shrink-0 w-8 text-right">{l.mentions}</div>
                </div>
              ))}
            </div>
          </div>
        )}
        {(v.mentions_by_country || []).length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-2">Menciones por país</div>
            <div className="space-y-1.5">
              {v.mentions_by_country.map((c) => (
                <div key={c.country} className="flex items-center gap-3 flex-wrap">
                  <div className="w-20 shrink-0 text-xs font-medium text-slate-800 inline-flex items-center gap-1">
                    <span>{({ AR: "🇦🇷", MX: "🇲🇽", CL: "🇨🇱", CO: "🇨🇴", ES: "🇪🇸", US: "🇺🇸" })[c.country] || "🌐"}</span>{c.country}
                  </div>
                  <div className="flex-1 min-w-[80px]">
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-sky-500 transition-all" style={{ width: `${c.pct}%` }} />
                    </div>
                  </div>
                  <div className="font-mono text-[11px] font-semibold text-sky-700 tabular-nums shrink-0">{c.pct}%</div>
                  <div className="font-mono text-[10px] text-slate-500 shrink-0 w-8 text-right">{c.mentions}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function BacklinksQualityCard({ q }) {
  return (
    <section className="bg-white border border-slate-200 rounded-md p-5" data-testid="backlinks-quality-card">
      <div className="flex items-start gap-2 mb-3 flex-wrap">
        <h3 className="font-display text-sm font-semibold text-slate-900">Calidad de backlinks</h3>
        <span className="text-[10px] uppercase tracking-[0.16em] text-slate-400 font-semibold">Naturalidad + fuentes editoriales</span>
      </div>

      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="bg-emerald-50 border border-emerald-200 rounded-md p-2.5">
          <div className="text-[9px] uppercase tracking-wider text-emerald-700 font-semibold mb-0.5">DoFollow</div>
          <div className="font-mono text-base font-semibold text-emerald-700 tabular-nums">{q.dofollow_pct}<span className="text-[10px] opacity-70">%</span></div>
          <div className="text-[9px] text-emerald-600 font-mono">{q.dofollow_count?.toLocaleString()}</div>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-md p-2.5">
          <div className="text-[9px] uppercase tracking-wider text-slate-600 font-semibold mb-0.5">NoFollow</div>
          <div className="font-mono text-base font-semibold text-slate-700 tabular-nums">{q.nofollow_pct}<span className="text-[10px] opacity-70">%</span></div>
          <div className="text-[9px] text-slate-500 font-mono">{q.nofollow_count?.toLocaleString()}</div>
        </div>
        <div className="bg-violet-50 border border-violet-200 rounded-md p-2.5">
          <div className="text-[9px] uppercase tracking-wider text-violet-700 font-semibold mb-0.5">Editoriales AS&gt;40</div>
          <div className="font-mono text-base font-semibold text-violet-700 tabular-nums">{q.editorial_high_as_count}<span className="text-[10px] opacity-70"> / {q.editorial_high_as_target}</span></div>
          <div className="text-[9px] text-violet-500 font-mono">goal</div>
        </div>
      </div>

      {q.equity_lost_estimate > 0 && (
        <div className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 flex items-start gap-2" data-testid="equity-lost">
          <AlertCircle className="h-3.5 w-3.5 text-rose-700 shrink-0 mt-0.5" />
          <div className="text-[11px] text-rose-900 leading-relaxed">
            <span className="font-semibold">~{q.equity_lost_estimate.toLocaleString()} backlinks en limbo</span> por homepages/T&C duplicados y campañas sin redirect. Recuperables con 301s.
          </div>
        </div>
      )}

      {(q.top_sources || []).length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-2">Top fuentes editoriales</div>
          <div className="space-y-1">
            {q.top_sources.map((s) => (
              <div key={s.name} className="flex items-center gap-2.5 bg-slate-50 border border-slate-200 rounded-sm px-2.5 py-1.5" data-testid={`top-source-${s.name}`}>
                <div className="flex-1 min-w-0 text-xs font-medium text-slate-800 truncate">{s.name}</div>
                <span className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">{s.type}</span>
                <span className="font-mono text-xs font-semibold text-slate-900 tabular-nums">AS {s.authority}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}


/* ============== Visibilidad IA ============== */
function AiVisibilitySection({ rows }) {
  const grouped = useMemo(() => {
    const map = {};
    for (const r of rows) {
      if (!map[r.prompt]) map[r.prompt] = {};
      map[r.prompt][r.tool] = r;
    }
    return Object.entries(map);
  }, [rows]);

  const total = rows.length;
  const visible = rows.filter((r) => r.appears).length;
  const pct = total ? Math.round((visible / total) * 100) : 0;

  return (
    <div className="space-y-5" data-testid="ai-visibility-section">
      {/* KPI header */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="bg-white border border-slate-200 rounded-md p-4">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Visibilidad agregada</div>
          <div className="font-mono text-3xl font-semibold text-slate-900 tabular-nums">{pct}<span className="text-base text-slate-500">%</span></div>
          <div className="text-[11px] text-slate-500 mt-1 font-mono">{visible} / {total} apariciones</div>
        </div>
        {["ChatGPT", "Perplexity", "Google AIO"].map((tool) => {
          const tot = rows.filter((r) => r.tool === tool).length;
          const vis = rows.filter((r) => r.tool === tool && r.appears).length;
          const p = tot ? Math.round((vis / tot) * 100) : 0;
          const tone = TOOL_TONE[tool];
          return (
            <div key={tool} className={`${tone.bg} ${tone.border} border rounded-md p-4`}>
              <div className={`text-[10px] uppercase tracking-wider font-semibold mb-1 ${tone.text} flex items-center gap-1.5`}>
                <span className={`h-1.5 w-1.5 rounded-full ${tone.dot}`} /> {tool}
              </div>
              <div className={`font-mono text-3xl font-semibold tabular-nums ${tone.text}`}>{p}<span className="text-base opacity-70">%</span></div>
              <div className={`text-[11px] mt-1 font-mono ${tone.text} opacity-80`}>{vis} / {tot} prompts</div>
            </div>
          );
        })}
      </div>

      {/* Per-prompt breakdown */}
      <section className="bg-white border border-slate-200 rounded-md p-5">
        <div className="flex items-start gap-2 mb-4 flex-wrap">
          <h3 className="font-display text-sm font-semibold text-slate-900">Detalle por prompt</h3>
          <span className="text-[10px] uppercase tracking-[0.16em] text-slate-400 font-semibold">¿Aparecemos en la respuesta?</span>
        </div>
        <div className="space-y-3">
          {grouped.map(([prompt, tools]) => (
            <div key={prompt} className="border border-slate-200 rounded-md p-4 bg-slate-50/40" data-testid={`ai-prompt-${prompt.slice(0,40)}`}>
              <div className="font-medium text-slate-900 text-sm mb-3 leading-snug">«{prompt}»</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
                {["ChatGPT", "Perplexity", "Google AIO"].map((tool) => {
                  const r = tools[tool];
                  const tone = TOOL_TONE[tool];
                  if (!r) {
                    return (
                      <div key={tool} className="bg-white border border-dashed border-slate-200 rounded-md p-3">
                        <div className="text-[10px] uppercase text-slate-400 font-semibold mb-1">{tool}</div>
                        <div className="text-xs text-slate-400 italic">Sin checkear</div>
                      </div>
                    );
                  }
                  return (
                    <div key={tool} className={`bg-white border rounded-md p-3 ${r.appears ? tone.border : "border-rose-200"}`}>
                      <div className="flex items-center justify-between gap-2 mb-1.5">
                        <div className={`text-[10px] uppercase font-semibold tracking-wider flex items-center gap-1 ${r.appears ? tone.text : "text-slate-500"}`}>
                          <span className={`h-1.5 w-1.5 rounded-full ${r.appears ? tone.dot : "bg-slate-300"}`} /> {tool}
                        </div>
                        {r.appears ? (
                          <span className="text-[10px] font-mono font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-sm inline-flex items-center gap-1">
                            <CheckCircle2 className="h-3 w-3" /> SÍ {r.position ? `#${r.position}` : ""}
                          </span>
                        ) : (
                          <span className="text-[10px] font-mono font-semibold text-rose-700 bg-rose-50 border border-rose-200 px-1.5 py-0.5 rounded-sm inline-flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" /> NO
                          </span>
                        )}
                      </div>
                      {(r.competitors || []).length > 0 && (
                        <div className="mt-2">
                          <div className="text-[9px] uppercase text-slate-400 font-semibold tracking-wider mb-1">En su lugar aparecen</div>
                          <div className="flex flex-wrap gap-1">
                            {r.competitors.map((c) => (
                              <span key={c} className="text-[10px] bg-slate-100 border border-slate-200 text-slate-600 px-1.5 py-0.5 rounded-sm font-medium">{c}</span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          {grouped.length === 0 && (
            <div className="text-xs text-slate-400 italic text-center py-6">Sin prompts trackeados todavía.</div>
          )}
        </div>
      </section>
    </div>
  );
}

/* ============== Backlog técnico ============== */
const STATUS_META = {
  pending: { label: "Pendiente", icon: Circle, color: "text-slate-500", bg: "bg-slate-50 border-slate-200" },
  in_progress: { label: "En curso", icon: Loader2, color: "text-sky-700", bg: "bg-sky-50 border-sky-200" },
  done: { label: "Resuelto", icon: CheckCircle2, color: "text-emerald-700", bg: "bg-emerald-50 border-emerald-200" },
};
const PRIORITY_TONE = {
  high: "text-rose-700 bg-rose-50 border-rose-200",
  medium: "text-amber-700 bg-amber-50 border-amber-200",
  low: "text-slate-600 bg-slate-50 border-slate-200",
};

function BacklogSection({ tasks }) {
  const [filter, setFilter] = useState("all");
  const counts = useMemo(() => ({
    all: tasks.length,
    pending: tasks.filter((t) => t.status === "pending").length,
    in_progress: tasks.filter((t) => t.status === "in_progress").length,
    done: tasks.filter((t) => t.status === "done").length,
  }), [tasks]);
  const filtered = filter === "all" ? tasks : tasks.filter((t) => t.status === filter);
  const sorted = useMemo(() => [...filtered].sort((a, b) => {
    const order = { pending: 0, in_progress: 1, done: 2 };
    const so = (order[a.status] || 0) - (order[b.status] || 0);
    if (so !== 0) return so;
    const prio = { high: 0, medium: 1, low: 2 };
    return (prio[a.priority] || 2) - (prio[b.priority] || 2);
  }), [filtered]);

  const completion = counts.all ? Math.round((counts.done / counts.all) * 100) : 0;

  return (
    <div className="space-y-5" data-testid="backlog-section">
      {/* Progress header */}
      <section className="bg-white border border-slate-200 rounded-md p-5">
        <div className="flex items-start gap-2 mb-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <h3 className="font-display text-sm font-semibold text-slate-900">Backlog técnico</h3>
            <p className="text-[11px] text-slate-500 mt-0.5">Acciones concretas avanzando</p>
          </div>
          <div className="font-mono text-2xl font-semibold text-slate-900 tabular-nums">{completion}<span className="text-sm text-slate-500">%</span></div>
        </div>
        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full bg-emerald-500 transition-all" style={{ width: `${completion}%` }} />
        </div>
        <div className="flex gap-1.5 mt-3 flex-wrap" data-testid="backlog-filter">
          {[
            ["all", "Todas", "text-slate-700"],
            ["pending", "Pendientes", "text-slate-700"],
            ["in_progress", "En curso", "text-sky-700"],
            ["done", "Resueltas", "text-emerald-700"],
          ].map(([k, lbl, col]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`backlog-filter-${k}`}
              className={`text-xs px-2.5 py-1 rounded-sm border font-medium transition-colors ${filter === k ? "bg-slate-900 text-white border-slate-900" : `bg-white border-slate-200 ${col} hover:border-slate-400`}`}>
              {lbl} <span className="font-mono ml-1 opacity-70">{counts[k]}</span>
            </button>
          ))}
        </div>
      </section>

      {/* Task list */}
      <div className="space-y-2">
        {sorted.map((t) => {
          const sm = STATUS_META[t.status] || STATUS_META.pending;
          const Icon = sm.icon;
          return (
            <div key={t.id} className={`bg-white border ${sm.bg} rounded-md p-4 flex items-start gap-3`} data-testid={`backlog-task-${t.id}`}>
              <Icon className={`h-4 w-4 shrink-0 mt-0.5 ${sm.color} ${t.status === "in_progress" ? "animate-spin" : ""}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-start gap-2 flex-wrap">
                  <h4 className={`text-sm font-medium ${t.status === "done" ? "line-through text-slate-500" : "text-slate-900"}`}>{t.title}</h4>
                  <span className={`text-[10px] font-mono uppercase tracking-wider border px-1.5 py-0.5 rounded-sm font-semibold ${PRIORITY_TONE[t.priority] || PRIORITY_TONE.low}`}>{t.priority}</span>
                </div>
                {t.notes && <p className="text-xs text-slate-600 mt-1.5 leading-relaxed">{t.notes}</p>}
                <div className="flex items-center gap-3 mt-2 flex-wrap text-[10px] font-mono text-slate-500">
                  {t.owner && t.owner !== "—" && <span className="inline-flex items-center gap-1"><span className="h-4 w-4 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center text-[8px] font-semibold">{t.owner[0]}</span>{t.owner}</span>}
                  {t.due_date && <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" />{t.due_date}</span>}
                  <span className={`uppercase tracking-wider font-semibold ${sm.color}`}>{sm.label}</span>
                </div>
              </div>
            </div>
          );
        })}
        {sorted.length === 0 && (
          <div className="text-xs text-slate-400 italic text-center py-8 bg-white border border-dashed border-slate-200 rounded-md">Sin tareas en este filtro.</div>
        )}
      </div>
    </div>
  );
}
